import * as echarts from "echarts";
import moment from "moment-timezone";
import React, { useCallback, useEffect, useRef } from "react";
import { useSocket } from "src/components/SocketContext";
import {
  indexKeyVariants,
  normalizeIndexKey,
} from "src/constants/indexSymbols";
import { DataPoint } from "src/helpers";
import {
  formatNumber,
  getAllMonthChangeIndices,
} from "src/helpers/api/sharedFunction";
import {
  getIndexTick,
  IndexTickSnapshot,
  subscribeIndexTick,
} from "src/hooks/useIndexTicks";

interface LineChartProps {
  lineData: DataPoint[];
  darkMode: boolean;
  isAnalyzeStock?: boolean;
  isExpand?: boolean;
  isIndexExpand?: boolean;
  timeframe?: string;
  isOrderblock?: boolean;
  isDashboardModal?: boolean;
  selectedDate?: Date | null;
  symbolName?: string;
  isMarketAnalysis?: boolean;
  /** Index name to follow on the live index feed, e.g. "NIFTY 50". */
  indexSymbol?: string;
}

interface PeakReturnData {
  peakIndex: number;
  peakValue: number;
  returnPercentage: number;
}

type LtpMessage = {
  symbol_name: string;
  ltp: string;
  high: string;
  ltp_date: string;
  close: string;
};

type ChartDataItem =
  | number
  | {
      value: number;
      symbolSize?: number;
      itemStyle?: { color: string; shadowBlur?: number; shadowColor?: string };
      zlevel?: number;
    };

/**
 * A tick older than this is not a moving price, and the pulsing marker is dropped this
 * long after the last tick — so it is only ever on screen while the market is open.
 */
const LIVE_TICK_MAX_AGE_MS = 60_000;

/**
 * Whether a tick is a price moving right now. A socket payload carrying no timestamp has
 * just arrived; anything timestamped (including the DB prime, at most 30s behind while
 * the market is open) has to be recent.
 */
const isLiveTick = (tick: IndexTickSnapshot): boolean => {
  if (!tick.timestamp) return true;
  const age = Date.now() - new Date(tick.timestamp).getTime();
  return !Number.isNaN(age) && age < LIVE_TICK_MAX_AGE_MS;
};

const LineChart: React.FC<LineChartProps> = ({
  lineData,
  darkMode,
  isAnalyzeStock,
  isExpand,
  timeframe,
  isOrderblock,
  selectedDate,
  isDashboardModal,
  symbolName,
  isMarketAnalysis: _isMarketAnalysis,
  indexSymbol,
  // isIndexExpand
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.EChartsType | null>(null);
  const lastLtpRef = useRef<{
    date: string;
    value: number;
    color: string;
  } | null>(null);
  const { isConnected, socket, isMarketClosed } = useSocket();
  const formatDateToIST = (date: Date | string): string => {
    return moment.utc(date).tz("Asia/Kolkata").format("YYYY-MM-DD");
  };

  const calculateTrendLine = (data: number[]): number[] => {
    const x = data.map((_, i) => i);
    const n = data.length;
    const xSum = x.reduce((a, b) => a + b, 0);
    const ySum = data.reduce((a, b) => a + b, 0);
    const xMean = xSum / n;
    const yMean = ySum / n;

    const numerator = x.reduce(
      (sum, xi, i) => sum + (xi - xMean) * (data[i] - yMean),
      0,
    );
    const denominator = x.reduce((sum, xi) => sum + (xi - xMean) ** 2, 0);
    const slope = numerator / denominator;
    const intercept = yMean - slope * xMean;

    return data.map((_, i) => slope * i + intercept);
  };

  let closes = lineData.map((item) => item.close);

  const calculatePeakReturn = (
    selectedIndex: number,
    data: DataPoint[],
  ): PeakReturnData | null => {
    if (selectedIndex < 0 || selectedIndex >= data.length - 1) {
      return null;
    }

    const entryPrice = data[selectedIndex].close;
    const dataAfterSelected = data.slice(selectedIndex + 1);

    const peakHigh = Math.max(...dataAfterSelected.map((item) => item.high));

    if (peakHigh <= entryPrice) {
      return null;
    }

    const peakIndex = data.findIndex(
      (item, idx) => idx > selectedIndex && item.high === peakHigh,
    );

    if (peakIndex === -1) return null;

    const returnPercentage = ((peakHigh - entryPrice) / entryPrice) * 100;

    return {
      peakIndex,
      peakValue: peakHigh,
      returnPercentage,
    };
  };

  const dates = lineData.map((item) => formatDateToIST(item?.date));
  // Calculate minSpan and initial view based on timeframe
  let minSpan = 30; // No minimum span by default
  let start = 0;
  let end = 100;

  if (dates.length > 0) {
    const totalStart = new Date(dates[0]).getTime();
    const totalEnd = new Date(dates[dates.length - 1]).getTime();
    const totalDuration = totalEnd - totalStart;

    if (timeframe === "1D") {
      // For 1D timeframe, show last 12 months by default
      const twelveMonths = 1000 * 60 * 60 * 24 * 30 * 12; // approx 12 months
      minSpan = (twelveMonths / totalDuration) * 100;
      start = Math.max(0, 100 - minSpan);
      end = 100;
    } else if (timeframe === "1M") {
      // For 1M timeframe, show last 5 years by default
      const fiveYears = 1000 * 60 * 60 * 24 * 365 * 5; // approx 5 years
      const spanPercentage = (fiveYears / totalDuration) * 100;
      start = Math.max(0, 100 - spanPercentage);
      end = 100;
    } else if (timeframe === "3M") {
      // For 3M timeframe, show last 10 years by default
      const tenYears = 1000 * 60 * 60 * 24 * 365 * 10; // approx 10 years
      const spanPercentage = (tenYears / totalDuration) * 100;
      start = Math.max(0, 100 - spanPercentage);
      end = 100;
    }
  }
  // Selected date processing
  const selectedDateStr = selectedDate ? formatDateToIST(selectedDate) : null;
  const selectedDateIndex = selectedDateStr
    ? dates.indexOf(selectedDateStr)
    : -1;

  const average =
    closes.reduce((sum, val) => sum + val, 0) / (closes.length || 1);
  const trendLine = calculateTrendLine(closes);

  // Peak return calculation (only for dashboard modal)
  let peakReturnData: PeakReturnData | null = null;
  if (isDashboardModal) {
    peakReturnData = calculatePeakReturn(selectedDateIndex, lineData);
    if (peakReturnData && selectedDateIndex >= 0) {
      closes = lineData.map((item, idx) => {
        if (idx === selectedDateIndex) return item.close;
        if (idx > selectedDateIndex && idx <= peakReturnData!.peakIndex) {
          return item.high;
        }
        return item.close;
      });
    }
  }

  // Dashboard modal: Show only 10 days before selected date and 10 days after peak
  let displayDates = dates;
  let displayCloses: ChartDataItem[] = closes;
  let displayTrend = trendLine;

  if (isDashboardModal && peakReturnData && selectedDateIndex >= 0) {
    const startIndex = Math.max(selectedDateIndex - 10, 0);
    const endIndex = Math.min(peakReturnData.peakIndex + 10, dates.length - 1);

    displayDates = dates.slice(startIndex, endIndex + 1);
    displayCloses = closes.slice(startIndex, endIndex + 1);
    displayTrend = trendLine.slice(startIndex, endIndex + 1);
  }

  // Highlight today's date with a yellow dot for the LTP
  const todayStr = moment().tz("Asia/Kolkata").format("YYYY-MM-DD");

  displayCloses = displayCloses.map((c, i) => {
    const isToday = displayDates[i] === todayStr;
    const hasLiveLtp =
      lastLtpRef.current && lastLtpRef.current.date === displayDates[i];

    if ((isToday && isConnected) || hasLiveLtp) {
      const rawVal = typeof c === "object" ? c.value : c;
      const val = hasLiveLtp ? lastLtpRef.current!.value : rawVal;

      const prevC = i > 0 ? displayCloses[i - 1] : null;
      const prevVal =
        prevC !== null
          ? typeof prevC === "object"
            ? prevC.value
            : prevC
          : val;

      const color = hasLiveLtp
        ? lastLtpRef.current!.color
        : val >= prevVal
          ? "#10B981"
          : "#ff4d4f";

      return {
        value: val,
        symbolSize: 8,
        itemStyle: {
          color: color,
          shadowBlur: 10,
          shadowColor: color,
        },
        zlevel: 5,
      };
    }
    return c;
  });

  // Extract today's data for the ripple effect initialization
  const todayIndex = displayDates.indexOf(todayStr);
  const todayPoint = todayIndex !== -1 ? displayCloses[todayIndex] : null;
  const todayDataForRipple: { value: number; color: string } | null =
    todayPoint && typeof todayPoint === "object" && "itemStyle" in todayPoint
      ? {
          value: todayPoint.value,
          color: todayPoint.itemStyle?.color || "#ff4d4f",
        }
      : null;

  const getChartHeight = (): string => {
    if (isAnalyzeStock || isExpand) return "h-full";
    if (isOrderblock) return "h-full";
    return "h-85";
  };

  const getMarkLineConfig = () => {
    if (!isDashboardModal || !peakReturnData || selectedDateIndex < 0) {
      return;
    }

    const startIndex = Math.max(selectedDateIndex - 10, 0);
    const adjustedSelectedIndex = selectedDateIndex - startIndex;
    const adjustedPeakIndex = peakReturnData.peakIndex - startIndex;

    if (
      adjustedSelectedIndex < 0 ||
      adjustedSelectedIndex >= displayDates.length ||
      adjustedPeakIndex < 0 ||
      adjustedPeakIndex >= displayDates.length
    ) {
      return;
    }

    return {
      symbol: ["none", "arrow"],
      symbolSize: 8,
      animation: true,
      animationDuration: 300,
      lineStyle: {
        color: "#10B981",
        type: "dashed",
        width: 2,
      },
      label: {
        show: true,
        position: "middle",
        formatter: `+${peakReturnData.returnPercentage.toFixed(2)}%`,
        color: darkMode ? "#fff" : "#065F46",
        backgroundColor: darkMode
          ? "rgba(16, 185, 129, 0.3)"
          : "rgba(16, 185, 129, 0.15)",
        padding: [4, 8],
        borderRadius: 4,
        fontSize: 12,
        fontWeight: "bold",
      },
      data: [
        [
          {
            coord: [
              displayDates[adjustedSelectedIndex],
              displayCloses[adjustedSelectedIndex],
            ],
            name: `start_${selectedDateIndex}_${displayCloses[adjustedSelectedIndex]}`,
          },
          {
            coord: [displayDates[adjustedPeakIndex], peakReturnData.peakValue],
            name: `peak_${peakReturnData.peakIndex}_${peakReturnData.peakValue}`,
          },
        ],
      ],
    };
  };

  const tooltipFormatter = (
    params: { axisValue: string; data: number | { value: number } }[],
  ) => {
    if (!params || params.length === 0) return "";

    const point = params[0];
    const dateStr = point.axisValue;

    // Read at hover time, so the live session's slot always shows the current price —
    // including the day this chart opened itself, which is not in the fetched dates.
    const lastLtp = lastLtpRef.current;
    const liveLtp = lastLtp && lastLtp.date === dateStr ? lastLtp.value : null;
    if (liveLtp === null && !displayDates.includes(dateStr)) {
      return "";
    }
    const rawData = point.data;

    const value =
      liveLtp !== null
        ? liveLtp
        : typeof rawData === "object" && rawData !== null && "value" in rawData
          ? Number(rawData.value)
          : Number(rawData);

    const formattedValue = value.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    const dateIST = moment.tz(dateStr, "Asia/Kolkata").format("DD MMM YYYY");

    // Find the current date index in the full dates array
    const currentDateIndex = dates.indexOf(dateStr);

    // Determine if we should show "High" or "Close"
    let label = "Close";
    const todayStrTest = moment().tz("Asia/Kolkata").format("YYYY-MM-DD");

    if (dateStr === todayStrTest && isConnected) {
      label = "LTP";
    } else if (
      isDashboardModal &&
      peakReturnData &&
      selectedDateIndex >= 0 &&
      currentDateIndex > selectedDateIndex &&
      currentDateIndex <= peakReturnData.peakIndex
    ) {
      label = "High";
    }

    return `${dateIST}<br/>${label}: ${formattedValue}`;
  };

  const lastDataDateStr =
    displayDates.length > 0 ? displayDates[displayDates.length - 1] : null;
  const hasTodaysDate = lastDataDateStr === todayStr;

  const BUFFER_DAYS = isDashboardModal ? 0 : hasTodaysDate ? 15 : 0;

  const extendedDates = [...displayDates];

  if (extendedDates.length > 0 && BUFFER_DAYS > 0) {
    const lastDate = moment(extendedDates[extendedDates.length - 1]);

    for (let i = 1; i <= BUFFER_DAYS; i++) {
      extendedDates.push(lastDate.clone().add(i, "days").format("YYYY-MM-DD"));
    }
  }

  const isShortRange = extendedDates.length <= 45;
  const allMonthChangeIndices = getAllMonthChangeIndices(extendedDates);

  // If there are many months (e.g. > 28), step by 2 or 3 to avoid overcrowding
  const monthStep =
    allMonthChangeIndices.length > 48
      ? 3
      : allMonthChangeIndices.length > 28
        ? 2
        : 1;

  const selectedMonthIndices = allMonthChangeIndices.filter(
    (_, i) => i % monthStep === 0,
  );

  // For short ranges (<= 45 days), pick ~6-8 evenly spaced day indices
  const shortRangeStep = Math.max(1, Math.floor(extendedDates.length / 7));

  const extendedCloses = [
    ...displayCloses,
    ...new Array(BUFFER_DAYS).fill("-"),
  ];

  const extendedTrend = [...displayTrend, ...new Array(BUFFER_DAYS).fill("-")];

  const option = {
    backgroundColor: "transparent",

    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "line",
        snap: true,
      },
      formatter: tooltipFormatter,
    },

    grid: {
      top: isAnalyzeStock ? "8%" : 10,
      left: 16,
      right: 4,
      bottom: 2,
      containLabel: true,
    },

    xAxis: {
      type: "category",
      data: extendedDates,
      boundaryGap: true,
      axisLabel: {
        hideOverlap: true,
        rotate: 35,
        margin: 12,
        formatter(value: string) {
          return isShortRange
            ? moment(value).format("D MMM")
            : moment(value).format("MMM 'YY");
        },
        interval: (index: number) => {
          if (isShortRange) {
            return index % shortRangeStep === 0;
          }
          return selectedMonthIndices.includes(index);
        },
      },
      axisTick: { alignWithLabel: true },
      splitLine: { show: false },
    },

    yAxis: {
      type: "value",
      scale: true,
      splitLine: { show: false },
      axisLabel: { formatter: (v: number) => formatNumber(v, true) },
    },

    dataZoom: [
      {
        type: "inside",
        start: start,
        end: end,
        minSpan: minSpan,
        maxSpan: 100,
        zoomLock: isDashboardModal || !isExpand,
      },
      {
        show: false,
        type: "slider",
        start: start,
        end: end,
        minSpan: minSpan,
        maxSpan: 100,
        zoomLock: !isExpand,
      },
    ],

    series: [
      // MAIN LINE
      {
        name: "Close Price",
        type: "line",
        symbol: "none",
        connectNulls: false,
        smooth: true,
        data: extendedCloses,

        lineStyle: {
          width: 1,
          color: darkMode ? "#ffffff" : "#090909",
          shadowColor: darkMode
            ? "rgba(255, 255, 255, 0.35)"
            : "rgba(0, 0, 0, 0.25)",
          shadowBlur: 10,
          shadowOffsetX: 2,
          shadowOffsetY: 2,
        },

        areaStyle: {
          opacity: darkMode ? 0.25 : 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: darkMode
                ? "rgba(255, 255, 255, 0.25)"
                : "rgba(187,195,199,1)",
            },
            { offset: 1, color: "rgba(255, 255, 255, 0)" },
          ]),
        },

        emphasis: {
          focus: "series",
          lineStyle: {
            width: 1.5,
            shadowColor: darkMode
              ? "rgba(255, 255, 255, 0.6)"
              : "rgba(0, 0, 0, 0.4)",
            shadowBlur: 15,
          },
        },

        animationDuration: 800,
        animationEasing: "cubicOut",

        markLine: getMarkLineConfig(),
      },

      // AVERAGE LINE (not shown in dashboard modal)
      ...(!isDashboardModal
        ? [
            {
              name: "Average",
              type: "line" as const,
              data: new Array(displayCloses.length).fill(average),
              symbol: "none",
              lineStyle: {
                color: "#6c757d",
                width: 1.5,
                shadowColor: darkMode
                  ? "rgba(255, 255, 255, 0.15)"
                  : "rgba(0, 0, 0, 0.15)",
                shadowBlur: 4,
                shadowOffsetY: 1,
              },
              markPoint: {
                symbol: "circle",
                symbolSize: 6,
                itemStyle: {
                  color: "#adb5bd",
                  borderColor: darkMode ? "#000" : "#fff",
                  borderWidth: 1,
                  shadowBlur: 4,
                  shadowColor: "rgba(0, 0, 0, 0.2)",
                },
                label: {
                  show: true,
                  color: darkMode ? "#f8f9fa" : "#212529",
                  backgroundColor: darkMode
                    ? "rgba(0,0,0,0.6)"
                    : "rgba(255,255,255,0.8)",
                  borderColor: "#6c757d",
                  borderWidth: 0.2,
                  borderRadius: 4,
                  padding: [4, 8],
                  position: "top",
                  align: "left",
                  distance: 6,
                  formatter: "52 week Avg. Price: " + average.toFixed(2),
                },
                data: [
                  {
                    coord: [displayDates[0], average],
                    value: average.toFixed(2),
                  },
                ],
              },
            },
          ]
        : []),

      ...(!isDashboardModal
        ? [
            {
              name: "LTP_Ripple",
              type: "effectScatter",
              coordinateSystem: "cartesian2d",
              data: (() => {
                // The marker means "this price is moving right now", so a closed market
                // has none at all.
                if (!isConnected || isMarketClosed) return [];
                if (
                  lastLtpRef.current &&
                  displayDates.includes(lastLtpRef.current.date)
                ) {
                  return [
                    {
                      value: [
                        lastLtpRef.current.date,
                        lastLtpRef.current.value,
                      ],
                      itemStyle: {
                        color: lastLtpRef.current.color,
                        shadowColor: lastLtpRef.current.color,
                      },
                    },
                  ];
                }
                if (todayDataForRipple) {
                  return [
                    {
                      value: [todayStr, todayDataForRipple.value],
                      itemStyle: {
                        color: todayDataForRipple.color,
                        shadowColor: todayDataForRipple.color,
                      },
                    },
                  ];
                }

                return [];
              })(),
              symbolSize: 8,
              rippleEffect: {
                brushType: "stroke", // Single outer ring effect
                scale: 4, // large the ring expands
                period: 2, // Speed of the expansion
                number: 1, // Ensures only one wave is visible at a time
              },
              itemStyle: {
                color: "#ff4d4f", // Red color
                shadowBlur: 10,
                shadowColor: "#ff4d4f",
              },
              zlevel: 5, // Ensure it is on top of everything
            },
          ]
        : []),

      // TREND LINE (not shown in dashboard modal)
      ...(!isDashboardModal
        ? [
            {
              name: "Trend Line",
              type: "line" as const,
              data: extendedTrend,
              symbol: "none",
              lineStyle: {
                type: "dashed" as const,
                color: "#FD1050",
                width: 1,
                shadowColor: "rgba(253, 16, 80, 0.4)",
                shadowBlur: 8,
                shadowOffsetY: 2,
              },
              emphasis: {
                focus: "series" as const,
                lineStyle: {
                  width: 2,
                  shadowColor: "rgba(253, 16, 80, 0.7)",
                  shadowBlur: 14,
                },
              },
            },
          ]
        : []),
    ],
  };

  useEffect(() => {
    if (chartRef.current) {
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(
          chartRef.current,
          darkMode ? "dark" : "light",
        );
      }
      chartInstance.current.setOption(option, true);
    }

    const resizeObserver = new ResizeObserver(() => {
      chartInstance.current?.resize();
    });

    if (chartRef.current) {
      resizeObserver.observe(chartRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- option is derived from lineData and darkMode
  }, [
    lineData,
    darkMode,
    selectedDate,
    isDashboardModal,
    isExpand,
    isConnected,
    isMarketClosed,
  ]);

  /**
   * Patches the live point in place — series data only, so the user's zoom/pan and
   * everything else in the option are left untouched.
   */
  const applyLivePoint = useCallback(
    (
      ltpDateStr: string,
      newLtp: number,
      { extendAxis = false, showRipple = true } = {},
    ) => {
      if (!chartInstance.current || !Number.isFinite(newLtp)) return;

      const currentOption =
        chartInstance.current.getOption() as echarts.EChartsCoreOption & {
          xAxis?: { data: string[] }[];
          series?: { data: ChartDataItem[] }[];
        };
      if (!currentOption?.series?.[0] || !currentOption?.xAxis?.[0]) return;

      const datesData = [...currentOption.xAxis[0].data];
      let targetIndex = datesData.indexOf(ltpDateStr);
      let axisExtended = false;

      if (targetIndex === -1) {
        // The daily table has no row for the current session yet. Give the live price a
        // slot of its own rather than dropping it, the way the candle chart opens a
        // forming bar. Only ever forward: a stale tick resolves to a drawn date.
        const lastDate = datesData[datesData.length - 1];
        if (!extendAxis || !lastDate || ltpDateStr <= lastDate) return;

        datesData.push(ltpDateStr);
        targetIndex = datesData.length - 1;
        axisExtended = true;
      }

      const closesData = [...currentOption.series[0].data];
      const prevC = closesData[targetIndex - 1];
      const prevPrice =
        targetIndex > 0
          ? typeof prevC === "object"
            ? prevC.value
            : prevC
          : newLtp;

      const isBullish = newLtp >= prevPrice;
      const dynamicColor = isBullish ? "#10B981" : "#ff4d4f"; // Green vs Red

      closesData[targetIndex] = {
        value: newLtp,
        itemStyle: { color: dynamicColor },
      };

      lastLtpRef.current = {
        date: ltpDateStr,
        value: newLtp,
        color: dynamicColor,
      };

      chartInstance.current.setOption({
        ...(axisExtended ? { xAxis: [{ data: datesData }] } : {}),
        series: [
          {
            name: "Close Price",
            data: closesData,
          },
          ...(!isDashboardModal
            ? [
                {
                  name: "LTP_Ripple",
                  type: "effectScatter",
                  data:
                    showRipple && !isMarketClosed
                      ? [
                          {
                            value: [ltpDateStr, newLtp],
                            itemStyle: {
                              color: dynamicColor,
                              shadowColor: dynamicColor,
                            },
                          },
                        ]
                      : [],
                },
              ]
            : []),
        ],
      });
    },
    [isDashboardModal, isMarketClosed],
  );

  useEffect(() => {
    if (!socket || !isConnected || !symbolName) return;
    const handleChartStockUpdate = (messages: LtpMessage[]) => {
      const updated = messages.find((m) => m.symbol_name === symbolName);
      if (!updated) return;

      applyLivePoint(formatDateToIST(updated.ltp_date), Number(updated.ltp));
    };

    socket.on("stock_update", handleChartStockUpdate);
    return () => {
      socket.off("stock_update", handleChartStockUpdate);
    };
  }, [socket, isConnected, symbolName, applyLivePoint]);

  // Index feed: same patch path, driven by the shared index-tick hub.
  useEffect(() => {
    if (!indexSymbol) return;

    // On a symbol switch the new prop lands one render before the new data; applying
    // the new index's price to the old series would spike the line.
    const dataSymbol = lineData[lineData.length - 1]?.symbol;
    if (
      !dataSymbol ||
      !indexKeyVariants(dataSymbol).includes(normalizeIndexKey(indexSymbol))
    ) {
      return;
    }

    // The pulsing marker lasts only as long as ticks keep coming: once they stop, the
    // market has closed and it is dropped.
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    const dropRipple = () => {
      idleTimer = null;
      const chart = chartInstance.current;
      if (!chart || chart.isDisposed()) return;
      chart.setOption({ series: [{ name: "LTP_Ripple", data: [] }] });
    };

    const handleIndexTick = (tick: IndexTickSnapshot) => {
      const tickDate = (
        tick.timestamp
          ? moment(tick.timestamp).tz("Asia/Kolkata")
          : moment().tz("Asia/Kolkata")
      ).format("YYYY-MM-DD");

      const live = isLiveTick(tick);
      if (idleTimer) clearTimeout(idleTimer);
      if (live) idleTimer = setTimeout(dropRipple, LIVE_TICK_MAX_AGE_MS);

      // Indexes are followed live even before the session's daily row exists, so this
      // path may open the current day's slot itself.
      applyLivePoint(tickDate, tick.ltp, {
        extendAxis: true,
        showRipple: live,
      });
    };

    const seed = getIndexTick(indexSymbol);
    if (seed) handleIndexTick(seed);

    const unsubscribe = subscribeIndexTick(indexSymbol, handleIndexTick);
    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      unsubscribe();
    };
  }, [indexSymbol, applyLivePoint, lineData]);

  return (
    <div
      ref={chartRef}
      id="line-chart"
      className={`w-full ${getChartHeight()}`}
      style={{ transform: "scale(0.98)" }}
    />
  );
};

export default React.memo(LineChart);
